import React, { useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import AdjustmentRequestService, { DebtAdjustmentRequestType } from "../services/adjustment-request-service";
import BranchService, { Branch } from "../../branch/services/branch-service";
import ClothingCategoryService, { ClothingCategory, CLOTHING_CATEGORY_STATUS } from "../../clothing-categories/services/clothing-category-service";
import { isSuccess } from "../../../models/response/base-response";

export interface DebtCategoryOption {
  category: string;
  category_id: number | null;
  /** Nợ cuối kỳ đang có (từ báo cáo đối soát) — làm căn cứ cho chế độ "Cân bằng đối soát". */
  closing_debt: number;
}

type Mode = "balance" | "backfill";

interface Row {
  category: string;
  category_id: number | null;
  /** Chỉ có ở chế độ Cân bằng — nợ hiện có của đúng loại đồ này, để chặn nhập vượt quá. */
  closing_debt?: number;
  quantity: string;
}

interface Props {
  open: boolean;
  customerId: number;
  customerName: string;
  /** Các loại đồ + nợ cuối kỳ hiện có, lấy từ báo cáo đối soát đang xem. */
  categoryOptions: DebtCategoryOption[];
  /** Prefill khi mở từ nút trên 1 dòng cụ thể — vào thẳng chế độ Cân bằng, chỉ điền sẵn số cho dòng này. */
  initialCategory?: string;
  onClose: () => void;
  onCreated: () => void;
}

const numberFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const fmt = (v: number) => numberFmt.format(Number(v) || 0);

function useDebounce<T>(value: T, delay = 400): T {
  const [val, setVal] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setVal(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return val;
}

/**
 * Tạo YÊU CẦU điều chỉnh công nợ đồ — đi qua luồng duyệt (`/adjustment-requests/debt` + giám sát duyệt ở
 * hàng đợi), KHÔNG gọi thẳng `/customer-item-debt/adjust`. Xem `yeu-cau-dieu-chinh-ky-thuat.md` §5.
 *
 * 1 LẦN BẤM = 1 PHIẾU mang nhiều loại đồ (`items`), dùng chung 1 `reason` — khớp đúng thực tế: "cân bằng
 * cuối kỳ" hay "đồng bộ nợ cũ" là MỘT hành động/một đợt xử lý, không phải nhiều đề xuất rời rạc ghép lại.
 * `request_type` gắn theo phiếu để phân biệt với điều chỉnh nợ/trả thông thường.
 *
 * 2 chế độ tách bạch theo đúng 2 tình huống thực tế:
 *  - `balance` (Cân bằng đối soát cuối kỳ): bảng liệt kê SẴN mọi loại đồ đang có nợ cuối kỳ trong báo cáo
 *    (số lượng mặc định = toàn bộ nợ, sửa được từng dòng, để 0 = bỏ qua dòng đó) — luôn TRỪ/CỘNG BÙ để
 *    kéo về 0, không có chiều ngược lại.
 *  - `backfill` (Đồng bộ nợ cũ): CHỈ dùng để GHI NỢ (không có chiều "ghi trả") — thanh tìm loại đồ
 *    (server-side, giống cách mobile tìm loại đồ cho phiếu kiểm đếm), chọn xong add thành 1 dòng, nhập số
 *    lượng, có thể thêm nhiều dòng nhiều loại đồ khác nhau.
 */
const CreateDebtAdjustmentRequestModal: React.FC<Props> = ({
  open,
  customerId,
  customerName,
  categoryOptions,
  initialCategory,
  onClose,
  onCreated,
}) => {
  const { fetch: createDebt } = AdjustmentRequestService.createDebt();
  const { fetch: fetchMyBranches } = BranchService.myBranches();
  const { fetch: searchCategories } = ClothingCategoryService.list();

  const [mode, setMode] = useState<Mode>("balance");
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchId, setBranchId] = useState<number | "">("");
  const [balanceRows, setBalanceRows] = useState<Row[]>([]);
  const [backfillRows, setBackfillRows] = useState<Row[]>([]);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [searchResults, setSearchResults] = useState<ClothingCategory[]>([]);
  const [searching, setSearching] = useState(false);

  const balanceCategories = categoryOptions.filter((c) => c.closing_debt !== 0);

  useEffect(() => {
    if (!open) return;
    setMode(initialCategory ? "balance" : balanceCategories.length ? "balance" : "backfill");
    setBalanceRows(
      balanceCategories.map((c) => ({
        category: c.category,
        category_id: c.category_id,
        closing_debt: c.closing_debt,
        // Mở từ nút trên 1 dòng cụ thể (initialCategory) → chỉ điền sẵn đúng dòng đó, các dòng khác để 0
        // (bảng vẫn hiện đủ để họ tự mở rộng nếu muốn xử lý thêm loại khác trong cùng phiếu).
        quantity: !initialCategory || c.category === initialCategory ? String(Math.abs(c.closing_debt)) : "",
      })),
    );
    setBackfillRows([]);
    setSearchTerm("");
    setSearchResults([]);
    setReason("");
    setBranchId("");
    (async () => {
      const res = await fetchMyBranches();
      if (res && isSuccess(res)) {
        const raw: any = res.data;
        const arr: any[] = Array.isArray(raw) ? raw : (raw?.list ?? raw?.data ?? []);
        setBranches(arr.filter(Boolean));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCategory]);

  // Tìm loại đồ server-side (chỉ chế độ Đồng bộ nợ cũ) — giống cách mobile tìm loại đồ cho phiếu kiểm đếm.
  useEffect(() => {
    if (!open || mode !== "backfill" || !debouncedSearch.trim()) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setSearching(true);
      const res = await searchCategories({ key_search: debouncedSearch.trim(), status: CLOTHING_CATEGORY_STATUS.ACTIVE });
      if (!cancelled) {
        setSearchResults(res && isSuccess(res) ? ((res.data as any) || []) : []);
        setSearching(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, mode, open]);

  if (!open) return null;

  const addBackfillRow = (c: ClothingCategory) => {
    if (backfillRows.some((r) => r.category === c.name)) {
      showToast({ type: "warning", message: "Loại đồ này đã có trong danh sách" });
      return;
    }
    setBackfillRows((prev) => [...prev, { category: c.name, category_id: c.id, quantity: "1" }]);
    setSearchTerm("");
    setSearchResults([]);
  };

  const removeBackfillRow = (category: string) => {
    setBackfillRows((prev) => prev.filter((r) => r.category !== category));
  };

  const updateBalanceQty = (category: string, value: string) => {
    setBalanceRows((prev) =>
      prev.map((r) => {
        if (r.category !== category) return r;
        const capped = r.closing_debt ? Math.min(Number(value) || 0, Math.abs(r.closing_debt)) : Number(value) || 0;
        return { ...r, quantity: value === "" ? "" : String(capped) };
      }),
    );
  };

  const updateBackfillQty = (category: string, value: string) => {
    setBackfillRows((prev) => prev.map((r) => (r.category === category ? { ...r, quantity: value } : r)));
  };

  const activeRows = mode === "balance" ? balanceRows : backfillRows;
  const pendingRows = activeRows.filter((r) => Number(r.quantity) > 0);

  const handleSubmit = async () => {
    if (pendingRows.length === 0) {
      showToast({ type: "error", message: mode === "balance" ? "Vui lòng nhập số lượng ít nhất 1 loại đồ" : "Vui lòng thêm ít nhất 1 loại đồ" });
      return;
    }
    if (!reason.trim()) {
      showToast({ type: "error", message: "Vui lòng nhập lý do điều chỉnh" });
      return;
    }

    // Cân bằng: chiều tự suy từ dấu nợ hiện có (nợ dương → trừ, âm/giao dư → cộng bù), luôn kéo về 0.
    // Đồng bộ nợ cũ: LUÔN ghi nợ (dương) — không có chiều ghi trả.
    const items = pendingRows.map((row) => {
      const qty = Number(row.quantity);
      const signedQuantity = mode === "balance" ? (row.closing_debt! > 0 ? -qty : qty) : qty;
      return { category: row.category, category_id: row.category_id ?? undefined, quantity: signedQuantity };
    });
    const requestType: DebtAdjustmentRequestType = mode === "balance" ? "balance" : "backfill";

    setSubmitting(true);
    try {
      const res = await createDebt({
        customer_id: customerId,
        branch_id: branchId ? Number(branchId) : undefined,
        request_type: requestType,
        items,
        reason: reason.trim(),
      });
      if (res && (res.status === 200 || res.status === undefined)) {
        showToast({ type: "success", message: "Đã tạo yêu cầu điều chỉnh công nợ — chờ giám sát duyệt" });
        onCreated();
        onClose();
      } else {
        showToast({ type: "error", message: res?.message || "Tạo yêu cầu thất bại" });
      }
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Tạo yêu cầu thất bại" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[620px] max-w-[95vw] p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Tạo yêu cầu điều chỉnh công nợ</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-lg">
            ✕
          </button>
        </div>

        {/* Chọn tình huống trước — quyết định cả cách xây danh sách bên dưới */}
        <div className="flex rounded-lg border border-slate-200 overflow-hidden mb-4">
          <button
            type="button"
            onClick={() => setMode("balance")}
            className={`flex-1 px-3 py-2.5 text-sm font-semibold text-left ${
              mode === "balance" ? "bg-green-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            Cân bằng đối soát
            <div className={`text-xs font-normal ${mode === "balance" ? "text-green-100" : "text-slate-400"}`}>
              Xoá nợ cuối kỳ đang có
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode("backfill")}
            className={`flex-1 px-3 py-2.5 text-sm font-semibold text-left border-l border-slate-200 ${
              mode === "backfill" ? "bg-green-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            Đồng bộ nợ cũ
            <div className={`text-xs font-normal ${mode === "backfill" ? "text-green-100" : "text-slate-400"}`}>
              Chỉ ghi nợ, chưa có trên hệ thống
            </div>
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Khách hàng</label>
              <div className="text-sm font-medium text-slate-800 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                {customerName}
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Chi nhánh</label>
              <select
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">-- Chọn chi nhánh --</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>

          {mode === "balance" ? (
            balanceRows.length > 0 ? (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="flex justify-between px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase bg-slate-50 border-b border-slate-200">
                  <span>Loại đồ</span>
                  <span className="flex gap-6">
                    <span className="w-20 text-right">Đang nợ</span>
                    <span className="w-28 text-right">Cân bằng xuống</span>
                  </span>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                  {balanceRows.map((row) => (
                    <div key={row.category} className="flex justify-between items-center px-3 py-2 text-sm">
                      <span className="text-slate-700">{row.category}</span>
                      <span className="flex items-center gap-6">
                        <span className="w-20 text-right font-semibold text-amber-700">{fmt(row.closing_debt || 0)} cái</span>
                        <input
                          type="number"
                          min={0}
                          max={Math.abs(row.closing_debt || 0)}
                          value={row.quantity}
                          onChange={(e) => updateBalanceQty(row.category, e.target.value)}
                          className="w-28 border border-slate-200 rounded-lg px-2 py-1 text-sm text-right"
                        />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-400 italic px-3 py-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                Không có loại đồ nào còn nợ cuối kỳ trong báo cáo đang xem.
              </div>
            )
          ) : (
            <div>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Tìm loại đồ để thêm..."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
                {(searching || searchResults.length > 0) && searchTerm.trim() && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {searching ? (
                      <div className="px-3 py-2 text-sm text-slate-400">Đang tìm...</div>
                    ) : searchResults.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-400">Không tìm thấy loại đồ nào</div>
                    ) : (
                      searchResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => addBackfillRow(c)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                        >
                          {c.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {backfillRows.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden mt-2">
                  <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                    {backfillRows.map((row) => (
                      <div key={row.category} className="flex justify-between items-center px-3 py-2 text-sm">
                        <span className="text-slate-700">{row.category}</span>
                        <span className="flex items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            value={row.quantity}
                            onChange={(e) => updateBackfillQty(row.category, e.target.value)}
                            className="w-24 border border-slate-200 rounded-lg px-2 py-1 text-sm text-right"
                          />
                          <span className="text-slate-400 text-xs">cái</span>
                          <button
                            type="button"
                            onClick={() => removeBackfillRow(row.category)}
                            className="text-slate-400 hover:text-rose-600 px-1"
                            title="Bỏ dòng này"
                          >
                            ✕
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs text-slate-500 mb-1">Lý do (áp dụng cho mọi dòng ở trên)</label>
            <textarea
              className="w-full border border-slate-200 rounded-lg p-2 text-sm"
              rows={2}
              placeholder={
                mode === "balance"
                  ? "VD: chấp nhận thất thoát cuối tháng 9, công ty đền khách / bộ phận kho chịu trách nhiệm..."
                  : "VD: đồng bộ nợ cũ từ file Excel trước khi có hệ thống, tính tới 31/08/2026..."
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-semibold disabled:opacity-50"
          >
            Huỷ
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || pendingRows.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold disabled:opacity-50"
          >
            {submitting
              ? "Đang tạo..."
              : pendingRows.length > 1
                ? `Tạo yêu cầu (${pendingRows.length} loại đồ)`
                : "Tạo yêu cầu"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateDebtAdjustmentRequestModal;
